import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Toast from '../../components/ui/Toast';

const Profile = () => {
  const { userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [toast, setToast] = useState(null);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    city: '',
    preferredCategories: [],
    preferredLanguages: [],
    allowWhatsApp: false,
    emailNotifications: true,
  });

  const cities = [
    'الرياض',
    'جدة',
    'الدمام',
    'المدينة المنورة',
    'مكة المكرمة',
    'الطائف',
    'أبها',
    'خميس مشيط',
    'جازان',
    'نجران',
    'الباحة',
    'تبوك',
    'حائل',
    'بريدة',
    'سكاكا',
  ];

  const photographyCategories = [
    { value: 'event', label: 'تصوير المناسبات' },
    { value: 'product', label: 'تصوير المنتجات' },
    { value: 'portrait', label: 'التصوير الشخصي' },
    { value: 'food', label: 'تصوير الطعام' },
    { value: 'real-estate', label: 'تصوير العقارات' },
    { value: 'wedding', label: 'حفلات الزفاف' },
    { value: 'architectural', label: 'التصوير المعماري' },
    { value: 'children', label: 'تصوير الأطفال' },
  ];

  const languageOptions = [
    { value: 'arabic', label: 'العربية' },
    { value: 'english', label: 'الإنجليزية' },
  ];

  useEffect(() => {
    loadProfile();
  }, [userProfile]);

  const loadProfile = async () => {
    try {
      setLoading(true);
      if (!userProfile?.id) return;

      // Load user profile
      const { data: userData } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', userProfile.id)
        .single();

      // Load customer profile
      const { data: customerData } = await supabase
        .from('customer_profiles')
        .select('*')
        .eq('user_id', userProfile.id)
        .single();

      if (userData) {
        setFormData({
          fullName: userData.full_name || '',
          email: userData.email || '',
          phone: userData.phone || '',
          city: customerData?.location_preferences?.[0] || '',
          preferredCategories: customerData?.preferred_photography_types || [],
          preferredLanguages: customerData?.preferred_languages || [],
          allowWhatsApp: customerData?.allow_whatsapp || false,
          emailNotifications: customerData?.email_notifications !== false,
        });
      }
    } catch (error) {
      console.error('Failed to load profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      if (!userProfile?.id) return;

      // Update user profile
      const { error: userError } = await supabase
        .from('user_profiles')
        .update({
          full_name: formData.fullName,
          updated_at: new Date().toISOString(),
        })
        .eq('id', userProfile.id);

      if (userError) throw userError;

      // Update or create customer profile
      const customerUpdate = {
        user_id: userProfile.id,
        preferred_photography_types: formData.preferredCategories,
        location_preferences: formData.city ? [formData.city] : [],
        preferred_languages: formData.preferredLanguages,
        allow_whatsapp: formData.allowWhatsApp,
        email_notifications: formData.emailNotifications,
        updated_at: new Date().toISOString(),
      };

      const { error: customerError } = await supabase
        .from('customer_profiles')
        .upsert(customerUpdate, { onConflict: 'user_id' });

      if (customerError) throw customerError;

      setToast({ type: 'success', message: 'تم حفظ الملف الشخصي بنجاح' });
    } catch (error) {
      console.error('Failed to save profile:', error);
      setToast({ type: 'error', message: 'فشل حفظ الملف الشخصي' });
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !userProfile?.id) return;

    if (file.size > 2 * 1024 * 1024) {
      setToast({ type: 'error', message: 'حجم الصورة يجب أن يكون أقل من 2MB' });
      return;
    }

    try {
      setUploading(true);
      const fileExt = file.name.split('.').pop();
      const fileName = `${userProfile.id}-${Date.now()}.${fileExt}`;
      const filePath = fileName;

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      const { error: updateError } = await supabase
        .from('user_profiles')
        .update({ avatar_url: publicUrl })
        .eq('id', userProfile.id);

      if (updateError) throw updateError;

      setToast({ type: 'success', message: 'تم تحديث الصورة الشخصية بنجاح' });
      setTimeout(() => window.location.reload(), 1000);
    } catch (error) {
      console.error('Failed to upload avatar:', error);
      setToast({ type: 'error', message: 'فشل رفع الصورة: ' + (error.message || 'خطأ غير معروف') });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-slate-600">جاري التحميل...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900 mb-2">الملف الشخصي والإعدادات</h1>
        <p className="text-slate-600">إدارة معلوماتك الشخصية وتفضيلاتك</p>
      </div>

      {/* Profile Photo */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
        <h2 className="text-xl font-bold text-slate-900 mb-4">الصورة الشخصية</h2>
        <div className="flex items-center gap-6">
          <div className="relative w-24 h-24 flex-shrink-0">
            <div className="w-24 h-24 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden border-2 border-slate-200">
              {userProfile?.avatar_url ? (
                <img
                  src={userProfile.avatar_url}
                  alt={userProfile.full_name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <Icon name="User" size={48} className="text-primary" />
              )}
            </div>
            {uploading && (
              <div className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center">
                <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
              </div>
            )}
          </div>
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              onChange={handleAvatarUpload}
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="flex items-center gap-2 px-4 py-2 rounded-xl border border-slate-300 bg-white hover:bg-slate-50 text-sm font-medium text-slate-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Icon name={uploading ? 'Loader' : 'Upload'} size={16} className={uploading ? 'animate-spin text-primary' : 'text-slate-500'} />
              {uploading ? 'جاري الرفع...' : 'تغيير الصورة'}
            </button>
            <p className="text-sm text-slate-500 mt-2">JPG, PNG أو GIF (حد أقصى 2MB)</p>
          </div>
        </div>
      </div>

      {/* Personal Information */}
      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
        <h2 className="text-xl font-bold text-slate-900 mb-4">المعلومات الشخصية</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Input
            label=" الاسم الاول والاخير"
            value={formData.fullName}
            onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
            required
          />
          <Input
            label="البريد الإلكتروني"
            type="email"
            value={formData.email}
            disabled
            description="لا يمكن تغيير البريد الإلكتروني"
          />
          <Input
            label="رقم الهاتف"
            value={formData.phone}
            disabled
            description="لا يمكن تغيير رقم الهاتف"
          />
          <div>
            <label className="block text-sm font-medium text-slate-900 mb-2">المدينة</label>
            <select
              dir="rtl"
              value={formData.city}
              onChange={(e) => setFormData({ ...formData, city: e.target.value })}
              className="w-full h-10 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary text-right"
            >
              <option value="">اختر المدينة</option>
              {cities.map((city) => (
                <option key={city} value={city}>
                  {city}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-start">
        <Button
          variant="default"
          size="lg"
          onClick={handleSave}
          loading={saving}
          iconName="Save"
        >
          حفظ التغييرات
        </Button>
      </div>

      {toast && (
        <Toast
          type={toast.type}
          message={toast.message}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default Profile;
